/* https://brandplatform.smartphoto.com/general/organisms/filters/ */
import { expressEventListener, expressQuerySelector, expressQuerySelectorAll } from "../../common/html";
import { trackFiltersEvent } from "../../common/events";
import { getParameter, queryRemoveKeys, updateParameter } from "../../utils/querystring";
import { findInsertIndex } from "../../utils/dom-tree";
import { initShowMoreElements, IShowMoreElementsComponent } from "../technical/show-more-elements";
import { createCollapsable } from "../molecules/collapsable";
import { createCheckBoxSelectFilter } from "../filters/checkboxFilter";
import { createColorFilter } from "../filters/colorFilter";
import { createRangeFilter, IRangeFilter } from "../filters/rangeFilter";
import { TemplateType, TypeOfFilter } from "../../common/filter";

export interface IFilters {
	readonly disable: (filterQs: string, selectOptionValue: string[]) => void;
	readonly enable: (filterQs: string, selectOptionValue: string[]) => void;
	readonly getSelectOptions: (filterQs: string) => string[];
	readonly resetFiltersContainerEl: () => void;
}

export interface ICreateFilterOptions {
	readonly onFilter?: (
		multiselect: boolean,
		filterName: string,
		filterValue: string,
		checked: boolean,
		containerEl: HTMLElement,
		updateValue?: boolean,
	) => void;
	readonly resetAll?: (containerEl: HTMLElement) => void;
}

export function createFilters(containerEl: HTMLElement, opts: ICreateFilterOptions): IFilters {
	const selectedFiltersContainerEl = expressQuerySelector<HTMLElement>(
		containerEl,
		".technical-selectedfilters",
		false,
	);
	const noneSelectedLabelEl =
		selectedFiltersContainerEl &&
		expressQuerySelector(selectedFiltersContainerEl, ".technical-no-selected-filters", false);
	const { onFilter, resetAll } = opts;
	const showMoreElementsComponents: IShowMoreElementsComponent[] = [];

	const addFilterTemplate = (
		templateType: TemplateType,
		filterCategory: string,
		selectedFilterName: string,
		htmlOrColor: string,
		removeFilterEvent: any,
	): HTMLDivElement => {
		const div = document.createElement("div");
		div.dataset.filterforcategory = filterCategory;
		div.classList.add("m-filter-tag", "technical-filter-tag");
		div.dataset.filterforcategoryvalue = selectedFilterName;

		if (templateType === TemplateType.Text) {
			const p = document.createElement("p");
			p.classList.add("a-typography", "a-typography--p", "technical-filtertag-label");
			const content = document.createTextNode(htmlOrColor);
			p.appendChild(content);
			div.appendChild(p);
		} else {
			const iconSpan = document.createElement("span");
			iconSpan.classList.add("v2-a-icon", "v2-a-icon--unselected", "v2-a-icon--x-small", "v2-a-icon--" + htmlOrColor);
			if (htmlOrColor === "white" || htmlOrColor === "custom-nocolor") iconSpan.classList.add("v2-a-icon--outline");
			const icon = document.createElement("i");
			icon.classList.add("v2-a-icon__color");
			icon.setAttribute("style", "color:" + htmlOrColor);
			iconSpan.appendChild(icon);
			div.appendChild(iconSpan);
		}

		const closeIconSpan = document.createElement("span");
		closeIconSpan.dataset.eventalreadybinded = "true";
		closeIconSpan.classList.add("v2-a-icon", "v2-a-icon--x-small", "technical-close-icon", "v2-a-icon--hover");
		closeIconSpan.addEventListener("click", removeFilterEvent);
		const icon = document.createElement("i");
		icon.classList.add("v2-a-icon__icon");
		const svgEl = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		svgEl.classList.add("v2-a-icon__svg");
		const useEl = document.createElementNS("http://www.w3.org/2000/svg", "use");
		useEl.classList.add("v2-a-icon__use");
		useEl.setAttribute("href", "/svg/close-symbol.svg#close-symbol");
		svgEl.appendChild(useEl);
		icon.appendChild(svgEl);
		closeIconSpan.appendChild(icon);
		div.appendChild(closeIconSpan);

		return div;
	};

	const removeFromFilterContainer = (
		filtersContainerEl: HTMLElement,
		selectedFiltersContainerEl: HTMLElement,
		filterCategory: string,
		filterCategoryValue?: string,
	) => {
		const selector = !filterCategoryValue
			? `[data-filterforcategory="${filterCategory}"]`
			: `[data-filterforcategory="${filterCategory}"][data-filterforcategoryvalue="${filterCategoryValue}"]`;
		const selectedFilterCategoryEl = expressQuerySelector<HTMLElement>(selectedFiltersContainerEl, selector, false);
		if (selectedFilterCategoryEl) selectedFilterCategoryEl.remove();

		hideSelectedFilters(filtersContainerEl, selectedFiltersContainerEl);
	};

	const addTextToFilterContainer = (
		filtersContainerEl: HTMLElement,
		typeOfFilter: TypeOfFilter,
		selectedFiltersContainerEl: HTMLElement,
		filterCategory: string,
		selectedFilterValue: string,
		removeFilterEvent: any,
		selectedFilterName?: string,
	) => {
		showSelectedFilters(filtersContainerEl, selectedFiltersContainerEl);

		if (!selectedFiltersContainerEl) return;

		const selector =
			typeOfFilter === TypeOfFilter.Regular || typeOfFilter === TypeOfFilter.Slider
				? `[data-filterforcategory="${filterCategory}"]`
				: `[data-filterforcategory="${filterCategory}"][data-filterforcategoryvalue="${selectedFilterValue}"]`;
		const selectedFilterCategoryEl = expressQuerySelector<HTMLElement>(selectedFiltersContainerEl, selector, false);
		const selectedFilterDisplayName = selectedFilterName || selectedFilterValue;
		if (selectedFilterCategoryEl) {
			const filterCategoryContentEl = expressQuerySelector<HTMLElement>(
				selectedFilterCategoryEl,
				".technical-filtertag-label",
				true,
			);
			filterCategoryContentEl.innerHTML =
				typeOfFilter === TypeOfFilter.Slider
					? filterCategory + ": " + selectedFilterDisplayName
					: selectedFilterDisplayName;
			return;
		}

		const filter = addFilterTemplate(
			TemplateType.Text,
			filterCategory,
			selectedFilterValue,
			typeOfFilter === TypeOfFilter.Slider
				? filterCategory + ": " + selectedFilterDisplayName
				: selectedFilterDisplayName,
			removeFilterEvent,
		);
		const allSelectedColorFilters = expressQuerySelectorAll<HTMLElement>(
			selectedFiltersContainerEl,
			`[data-filterforcategory="${filterCategory}"]`,
		);
		if (allSelectedColorFilters.length) {
			allSelectedColorFilters[allSelectedColorFilters.length - 1].insertAdjacentElement("afterend", filter);
			return;
		}

		const filters = expressQuerySelectorAll(document, "[data-typeoffilter][data-filtercategory]") as HTMLElement[];
		const compare = (val1: HTMLElement) =>
			filters.findIndex((e) => val1.dataset.filterforcategory === e.dataset.filtercategory);
		const selectedFiltersChildren = expressQuerySelectorAll<HTMLElement>(
			selectedFiltersContainerEl,
			"[data-filterforcategory]",
		);
		const index = findInsertIndex(selectedFiltersChildren, filter, compare);
		const elementToInsertBefore = selectedFiltersChildren[index];
		if (!elementToInsertBefore || index <= -1 || index > selectedFiltersChildren.length) {
			selectedFiltersContainerEl.appendChild(filter);
			return;
		}
		elementToInsertBefore.insertAdjacentElement("beforebegin", filter);
	};

	const attachRemoveFilterEvent = (
		selectedFiltersContainerEl: HTMLElement,
		filterForCategory: string,
		removeEvent: any,
		filterForCategoryValue?: string,
	) => {
		const selectedFilterCategoryEl = expressQuerySelector<HTMLElement>(
			selectedFiltersContainerEl,
			!filterForCategoryValue
				? `[data-filterforcategory="${filterForCategory}"]`
				: `[data-filterforcategory="${filterForCategory}"][data-filterforcategoryvalue="${filterForCategoryValue}"]`,
			false,
		);

		if (selectedFilterCategoryEl) {
			const removeEl = expressQuerySelector<HTMLElement>(selectedFilterCategoryEl, ".technical-close-icon", false);
			if (removeEl && (!removeEl.dataset.eventalreadybinded || removeEl.dataset.eventalreadybinded !== "true")) {
				removeEl.dataset.eventalreadybinded = "true";
				removeEl.addEventListener("click", removeEvent);
			}
		}
	};

	const hideSelectedFilters = (filtersContainerEl: HTMLElement, selectedFiltersContainerEl: HTMLElement) => {
		const resetFilterEls = expressQuerySelectorAll<HTMLElement>(filtersContainerEl, ".o-filters__button-reset");

		if (!selectedFiltersContainerEl) {
			resetFilterEls.forEach((resetFilterEl) => resetFilterEl.classList.add("a-button--disabled"));
			return;
		}

		const filterContainerTagEls = expressQuerySelectorAll(
			selectedFiltersContainerEl.parentElement,
			".technical-filter-tag",
		);

		if (
			!filterContainerTagEls.length &&
			!selectedFiltersContainerEl.classList.contains("o-filters__selected-filters--none")
		) {
			selectedFiltersContainerEl.classList.add("o-filters__selected-filters--none");
			noneSelectedLabelEl && noneSelectedLabelEl.classList.remove("u-hide");
			resetFilterEls.forEach((resetFilterEl) => resetFilterEl.classList.add("a-button--disabled"));
		}
	};

	const toggleResetFilters = (filtersContainerEl: HTMLElement, force?: boolean) => {
		const resetFilterEls = expressQuerySelectorAll<HTMLElement>(filtersContainerEl, ".o-filters__button-reset");
		resetFilterEls.forEach((resetFilterEl) => resetFilterEl.classList.toggle("a-button--disabled", force));
	};

	const showSelectedFilters = (filtersContainerEl: HTMLElement, selectedFiltersContainerEl: HTMLElement) => {
		toggleResetFilters(filtersContainerEl, false);

		if (!selectedFiltersContainerEl) return;

		if (selectedFiltersContainerEl.classList.contains("o-filters__selected-filters--none")) {
			selectedFiltersContainerEl.classList.remove("o-filters__selected-filters--none");
			noneSelectedLabelEl && noneSelectedLabelEl.classList.add("u-hide");
		}
	};

	const updateParametersForUrl = (
		typeOfFilter: TypeOfFilter,
		filterQs: string,
		msg: string,
		removeFromUrl: boolean,
		analytics?: string,
		possibleValues?: string[],
	) => {
		trackFiltersEvent("FilterClicked", { filter: analytics ? analytics : `${filterQs}_${msg}` });

		if (!filterQs) return;

		let url: string;
		const value = encodeURIComponent(msg.toLowerCase());
		if (typeOfFilter === TypeOfFilter.Regular || typeOfFilter === TypeOfFilter.Slider) {
			url = removeFromUrl
				? updateParameter(filterQs.toLowerCase(), "")
				: updateParameter(filterQs.toLowerCase(), value);
		} else {
			const param = getParameter(filterQs.toLowerCase());
			let params = param ? param.split(",") : [];
			if (params.indexOf(value) < 0) params.push(value);
			else if (removeFromUrl) params = params.filter((f) => f !== value);

			if (possibleValues) {
				params = params.filter((x) => possibleValues.includes(x));
			}

			url = updateParameter(filterQs.toLowerCase(), params.join(","));
		}

		window.history.pushState({}, null, url.toLowerCase());
	};

	hideSelectedFilters(containerEl, selectedFiltersContainerEl);

	const collapsibleEls = expressQuerySelectorAll<HTMLElement>(containerEl, ".m-collapsable");
	const filterQsArr: string[] = [];
	const rangeFilterComponents: IRangeFilter[] = [];
	collapsibleEls.forEach((el) => {
		if (el.classList.contains("technical-selectedfilters")) return; // skip the selected filters collapsible

		const filterCategory = el.dataset.filtercategory;
		const elFilterQs = el.dataset.filterqs;

		const filterCollapsable = createCollapsable(el, {
			titleElSelector: ".m-collapsable__title",
			scrollIntoViewAfterOpen: false,
			openClass: "is-collapse-open",
		});
		const showMore = expressQuerySelector<HTMLElement>(el, ".technical-showmore", false);
		filterQsArr.push(elFilterQs);

		if (showMore) {
			filterQsArr.push(elFilterQs ? `showmore${elFilterQs.toLowerCase()}` : null);
			showMoreElementsComponents.push(
				initShowMoreElements(
					el,
					showMore,
					elFilterQs ? `showmore${elFilterQs.toLowerCase()}` : null,
					".technical-filter-value",
					-1,
					null,
					null,
				),
			);
		}

		const filterCheckboxEls = expressQuerySelectorAll<HTMLElement>(el, ".a-form-input--checkbox, .a-form-input--radio");
		const filterCheckboxPossibleValues = filterCheckboxEls
			.map((x) => [
				encodeURIComponent(x.dataset.filterelement)?.toLowerCase(),
				encodeURIComponent(x.dataset.filterkey)?.toLowerCase(),
			])
			.flat();
		filterCheckboxEls.forEach((filterEl) => {
			if (
				TypeOfFilter[el.dataset.typeoffilter] === TypeOfFilter.Regular ||
				TypeOfFilter[el.dataset.typeoffilter] === TypeOfFilter.MultiSelect
			)
				createCheckBoxSelectFilter(
					filterCollapsable,
					TypeOfFilter[el.dataset.typeoffilter],
					selectedFiltersContainerEl,
					elFilterQs,
					filterCategory,
					filterEl,
					filterCheckboxEls,
					filterCheckboxPossibleValues,
					containerEl,
					{
						updateParametersForUrl,
						removeFromFilterContainer,
						onFilter,
						attachRemoveFilterEvent,
						toggleResetFilters,
						addTextToFilterContainer,
					},
				);
		});

		const colorEls = expressQuerySelectorAll<HTMLElement>(el, ".technical-icon.technical-filter-value");
		const colorElsPossibleValues = colorEls.map((x) => encodeURIComponent(x.dataset.filterelement.toLowerCase()));
		colorEls.forEach((filterEl) => {
			createColorFilter(
				filterCollapsable,
				selectedFiltersContainerEl,
				elFilterQs,
				filterCategory,
				filterEl,
				containerEl,
				colorElsPossibleValues,
				{
					showSelectedFilters,
					addFilterTemplate,
					updateParametersForUrl,
					removeFromFilterContainer,
					onFilter,
					attachRemoveFilterEvent,
					toggleResetFilters,
				},
			);
		});

		const sliderEls = expressQuerySelectorAll<HTMLElement>(el, ".a-form-input--slider");
		sliderEls.forEach((filterEl) => {
			const showSliderEls = expressQuerySelectorAll<HTMLInputElement>(el, "input[type=checkbox],input[type=radio]");
			const rangeFilterComponent = createRangeFilter(
				filterCollapsable,
				selectedFiltersContainerEl,
				elFilterQs,
				filterCategory,
				filterEl,
				showSliderEls,
				containerEl,
				{
					updateParametersForUrl,
					removeFromFilterContainer,
					onFilter,
					attachRemoveFilterEvent,
					toggleResetFilters,
					addTextToFilterContainer,
				},
			);

			rangeFilterComponents.push(rangeFilterComponent);
		});
	});

	const resetFilters = expressQuerySelectorAll<HTMLElement>(containerEl.parentElement, ".technical-reset-filters");
	const resetFiltersContainerEl = () => {
		trackFiltersEvent("FilterReset");
		if (resetAll) resetAll(containerEl);

		collapsibleEls.forEach((collapsibleEl) => {
			if (
				collapsibleEl.classList.contains("is-collapse-open") &&
				!collapsibleEl.classList.contains("m-collapsable--sorting-top")
			)
				collapsibleEl.classList.remove("is-collapse-open");
		});

		const colorEls = expressQuerySelectorAll<HTMLElement>(containerEl, ".technical-icon");
		colorEls.forEach((colorEl) => {
			if (colorEl.classList.contains("v2-a-icon--selected")) {
				colorEl.classList.remove("v2-a-icon--selected");
				colorEl.classList.add("v2-a-icon--unselected");
			}
		});

		const filterEls = expressQuerySelectorAll<HTMLInputElement>(containerEl, ".technical-filter");
		filterEls.forEach((filterEl, index) => {
			const showMoreEl = expressQuerySelector<HTMLElement>(filterEl, ".technical-showmore", false);
			if (showMoreEl) {
				showMoreElementsComponents[index]?.resetElement && showMoreElementsComponents[index].resetElement();
			}
			const checkboxElsPerFilter = expressQuerySelectorAll<HTMLInputElement>(
				filterEl,
				"input[type=checkbox]:not(.technical-sorting-radio),input[type=radio]:not(.technical-sorting-radio)",
			);
			checkboxElsPerFilter.forEach((checkboxEl) => {
				checkboxEl.checked = false;
			});
		});

		rangeFilterComponents.forEach((showSliderEl) => showSliderEl.removeRangeFilter(false));

		const filterTagEls = expressQuerySelectorAll<HTMLElement>(selectedFiltersContainerEl, ".technical-filter-tag");
		filterTagEls.forEach((el) => el.remove());

		hideSelectedFilters(containerEl, selectedFiltersContainerEl);
		window.history.pushState({}, null, queryRemoveKeys(filterQsArr));
	};
	resetFilters.forEach((resetFilter) =>
		expressEventListener(
			resetFilter,
			"click",
			(_) => !resetFilter.classList.contains("a-button--disabled") && resetFiltersContainerEl(),
		),
	);
	const filterEl = expressQuerySelector(containerEl, ".technical-filters-container", false) || containerEl;
	filterEl.classList.remove("skeleton-loading");

	const disable = (filterQs: string, selectOptionValue: string[]) => {
		const foundfilter = rangeFilterComponents.find((x) => x.filterQs === filterQs);
		foundfilter && foundfilter.disable(selectOptionValue);
	};

	const enable = (filterQs: string, selectOptionValue: string[]) => {
		const foundfilter = rangeFilterComponents.find((x) => x.filterQs === filterQs);
		foundfilter && foundfilter.enable(selectOptionValue);
	};

	const getSelectOptions = (filterQs: string) => {
		const foundfilter = rangeFilterComponents.find((x) => x.filterQs === filterQs);
		return foundfilter ? foundfilter.getSelectOptions() : [];
	};

	return {
		resetFiltersContainerEl,
		disable,
		enable,
		getSelectOptions,
	} as IFilters;
}

export function sortGroupFilterValues(htmlFilterValues: HTMLElement[], htmlFilter: HTMLElement) {
	const disabledFilterValues = htmlFilterValues
		.filter((el) => el.classList.contains("a-form-input--disabled") || el.classList.contains("v2-a-icon--disabled"))
		.sort((a, b) => {
			if (a.dataset.selectedtext < b.dataset.selectedtext) return -1;
			if (a.dataset.selectedtext > b.dataset.selectedtext) return 1;
			return 0;
		});

	const enabledFilterValues = htmlFilterValues
		.filter((el) => !el.classList.contains("a-form-input--disabled") && !el.classList.contains("v2-a-icon--disabled"))
		.sort((a, b) => {
			if (a.dataset.selectedtext < b.dataset.selectedtext) return -1;
			if (a.dataset.selectedtext > b.dataset.selectedtext) return 1;
			return 0;
		});

	const filtersContainer = expressQuerySelector<HTMLElement>(htmlFilter, ".m-collapsable__content", true);
	const showMoreEl = expressQuerySelector<HTMLElement>(filtersContainer, ".technical-showmore", false);

	filtersContainer.innerHTML = "";
	enabledFilterValues.forEach((el) => {
		filtersContainer.insertAdjacentElement("beforeend", el);
	});
	disabledFilterValues.forEach((el) => {
		filtersContainer.insertAdjacentElement("beforeend", el);
	});
	if (showMoreEl) {
		const buttonTextEl = expressQuerySelector<HTMLElement>(showMoreEl, ".technical-link-text", false);
		const sorterFilterValues = expressQuerySelectorAll(filtersContainer, ".technical-filter-value");
		if (buttonTextEl.innerHTML === showMoreEl.dataset.moretext) {
			sorterFilterValues
				.slice(0, parseInt(showMoreEl.dataset.baseamount || "0", 10) - 1)
				.forEach((e) => e.classList.remove("u-hide-on-desktop-full"));
			sorterFilterValues
				.slice(0, parseInt(showMoreEl.dataset.baseamount || "0", 10) - 1)
				.forEach((e) => e.classList.remove("u-hide-on-mobile-full"));
			sorterFilterValues
				.slice(parseInt(showMoreEl.dataset.baseamount || "0", 10), sorterFilterValues.length)
				.forEach((e) => e.classList.add("u-hide-on-desktop-full"));
			sorterFilterValues
				.slice(parseInt(showMoreEl.dataset.baseamount || "0", 10), sorterFilterValues.length)
				.forEach((e) => e.classList.add("u-hide-on-mobile-full"));
		} else {
			sorterFilterValues.forEach((e) => e.classList.remove("u-hide-on-desktop-full"));
			sorterFilterValues.forEach((e) => e.classList.remove("u-hide-on-mobile-full"));
		}
		filtersContainer.insertAdjacentElement("beforeend", showMoreEl);
	}
}
